<template>
  <n-modal
    :show="show"
    @update:show="$emit('update:show', $event)"
    preset="card"
    title="Crop image"
    style="width: 90vw; max-width: 700px"
    :bordered="false"
    size="huge"
    role="dialog"
    aria-modal="true"
  >
    <div class="crop-modal-content" v-if="imageUrl">
      <div class="cropper-wrap">
        <Cropper
          ref="cropperRef"
          class="cropper"
          :src="imageUrl"
          :stencil-props="stencilProps"
          :canvas="false"
          @change="onCropperChange"
          @error="onCropperError"
        />
      </div>
      <div v-if="imageLoadError" class="crop-load-error">
        <p>Image could not be loaded. It may be blocked by CORS or the URL may be invalid.</p>
      </div>
      <div class="aspect-ratio-row">
        <span>Aspect ratio:</span>
        <n-radio-group v-model:value="aspectRatioPreset" size="small">
          <n-radio-button value="free">Free</n-radio-button>
          <n-radio-button value="4:5">4:5</n-radio-button>
          <n-radio-button value="5:7">5:7</n-radio-button>
          <n-radio-button value="7:5">7:5</n-radio-button>
          <n-radio-button value="1:1">1:1</n-radio-button>
          <n-radio-button value="16:9">16:9</n-radio-button>
          <n-radio-button value="9:16">9:16</n-radio-button>
        </n-radio-group>
      </div>
      <div class="crop-actions">
        <n-button @click="clearCrop" v-if="hasCrop">Clear crop</n-button>
        <span v-else></span>
        <div style="display: flex; gap: 8px">
          <n-button @click="$emit('update:show', false)">Cancel</n-button>
          <n-button type="primary" @click="confirm">Apply</n-button>
        </div>
      </div>
    </div>
  </n-modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { NModal, NButton, NRadioGroup, NRadioButton } from 'naive-ui'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'

export interface NormalizedCrop {
  cropLeft: number
  cropTop: number
  cropWidth: number
  cropHeight: number
}

const props = withDefaults(
  defineProps<{
    show: boolean
    imageUrl: string
    imageWidth?: number
    imageHeight?: number
    initialCrop?: NormalizedCrop | null
  }>(),
  { initialCrop: null }
)

const emit = defineEmits<{
  'update:show': [value: boolean]
  confirm: [crop: NormalizedCrop]
  clear: []
}>()

const cropperRef = ref<InstanceType<typeof Cropper> | null>(null)
const aspectRatioPreset = ref<string>('free')
const lastCoordinates = ref<{ left: number; top: number; width: number; height: number } | null>(null)
const imageLoadError = ref(false)

function onCropperError() {
  imageLoadError.value = true
}

const aspectRatioMap: Record<string, number | undefined> = {
  free: undefined,
  '4:5': 4 / 5,
  '5:7': 5 / 7,
  '7:5': 7 / 5,
  '1:1': 1,
  '16:9': 16 / 9,
  '9:16': 9 / 16
}

const stencilProps = computed(() => {
  const ratio = aspectRatioMap[aspectRatioPreset.value]
  return ratio !== undefined ? { aspectRatio: ratio } : {}
})

const hasCrop = computed(() => props.initialCrop != null)

watch(
  () => props.show,
  (visible) => {
    if (visible) {
      aspectRatioPreset.value = 'free'
      imageLoadError.value = false
    }
  }
)
watch(() => props.imageUrl, () => {
  imageLoadError.value = false
})

function onCropperChange({ coordinates }: { coordinates: { left: number; top: number; width: number; height: number } }) {
  lastCoordinates.value = coordinates
}

function confirm() {
  const cropper = cropperRef.value
  if (!cropper) return
  const result = (cropper as any).getResult?.()
  if (!result?.coordinates) {
    emit('update:show', false)
    return
  }
  const c = result.coordinates as { left: number; top: number; width: number; height: number }
  const imageSize = result.image ?? { width: props.imageWidth ?? 1, height: props.imageHeight ?? 1 }
  const iw = imageSize.width || 1
  const ih = imageSize.height || 1
  emit('confirm', {
    cropLeft: c.left / iw,
    cropTop: c.top / ih,
    cropWidth: c.width / iw,
    cropHeight: c.height / ih
  })
  emit('update:show', false)
}

function clearCrop() {
  emit('clear')
  emit('update:show', false)
}
</script>

<style scoped>
.crop-modal-content {
  min-height: 400px;
}
.cropper-wrap {
  width: 100%;
  height: 400px;
  min-height: 400px;
  background: #1a1a1a;
  border-radius: 8px;
  overflow: hidden;
}
/* Explicit dimensions required by vue-advanced-cropper for image and stencil to render */
.cropper {
  width: 100%;
  height: 100%;
  min-height: 400px;
}
.crop-load-error {
  margin-top: 8px;
  padding: 12px;
  background: rgba(255, 100, 100, 0.1);
  border-radius: 8px;
  color: var(--error-color, #d03050);
  font-size: 14px;
}
.aspect-ratio-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  flex-wrap: wrap;
}
.crop-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 16px;
}
</style>
